import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, Archive, Flame, Trophy, BarChart3, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useStacks, useAuth } from '@/lib/hooks';
import { STACK_ICONS, STACK_COLORS } from '@/lib/store';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { SevenDayStrip } from '@/components/SevenDayStrip';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

export function StackDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { stacks, getStackProgress, archiveStack, isLoading } = useStacks();
    const { user } = useAuth();

    const [selectedRange, setSelectedRange] = useState('7');
    const [archiving, setArchiving] = useState(false);

    const stack = stacks.find(s => s.id === id);
    const progress = getStackProgress(id);
    const isPro = user?.is_pro || false;

    if (isLoading) {
        return (
            <div className="max-w-lg mx-auto px-4 py-6 flex items-center justify-center min-h-[50vh]">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!stack || !progress) {
        return (
            <div className="max-w-lg mx-auto px-4 py-6">
                <p className="text-muted-foreground text-center">Stack not found</p>
            </div>
        );
    }

    const icon = STACK_ICONS.find(i => i.name === stack.icon) || STACK_ICONS[0];
    const color = STACK_COLORS.find(c => c.name === stack.color) || STACK_COLORS[0];

    const avgItems = progress.history7Day
        ? (progress.history7Day.reduce((sum, d) => sum + d.itemsCompleted, 0) / 7).toFixed(1)
        : 0;

    const handleArchive = async () => {
        setArchiving(true);
        try {
            await archiveStack(id);
            toast('Stack archived');
            navigate('/stacks');
        } catch (err) {
            toast.error(err.message);
        } finally {
            setArchiving(false);
        }
    };

    const handleRangeChange = (value) => {
        if (value === '90' && !isPro) {
            toast('Pro feature', { description: '90-day view requires Pro' });
            return;
        }
        setSelectedRange(value);
    };

    return (
        <div className="max-w-lg mx-auto px-4 py-6">
            {/* Header */}
            <header className="flex items-center gap-3 mb-6">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate('/stacks')}
                    className="-ml-2"
                >
                    <ArrowLeft className="w-5 h-5" />
                </Button>

                <div className="flex-1 flex items-center gap-3">
                    <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center text-xl",
                        color.lightClass
                    )}>
                        {icon.emoji}
                    </div>
                    <h1 className="font-bold text-lg text-foreground truncate">
                        {stack.name}
                    </h1>
                </div>

                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate(`/stacks/${id}/edit`)}
                >
                    <Edit2 className="w-5 h-5" />
                </Button>
            </header>

            {/* Stats cards */}
            <div className="grid grid-cols-3 gap-2 mb-6">
                <Card>
                    <CardContent className="p-3 text-center">
                        <Flame className="w-4 h-4 text-warning mx-auto mb-1" />
                        <p className="text-xl font-bold text-foreground">{progress.streak}</p>
                        <p className="text-[10px] text-muted-foreground">Current</p>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-3 text-center">
                        <Trophy className="w-4 h-4 text-chart-3 mx-auto mb-1" />
                        <p className="text-xl font-bold text-foreground">{progress.bestStreak}</p>
                        <p className="text-[10px] text-muted-foreground">Best</p>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-3 text-center">
                        <BarChart3 className="w-4 h-4 text-primary mx-auto mb-1" />
                        <p className="text-xl font-bold text-foreground">{avgItems}</p>
                        <p className="text-[10px] text-muted-foreground">Avg/day</p>
                    </CardContent>
                </Card>
            </div>

            {/* 7-day strip */}
            <Card className="mb-6">
                <CardContent className="p-4">
                    <h3 className="text-xs font-medium text-muted-foreground mb-3">Last 7 days</h3>
                    <div className="flex justify-center">
                        <SevenDayStrip
                            history={progress.history7Day || []}
                            colorName={stack.color}
                            size="large"
                            showLabels={true}
                        />
                    </div>
                </CardContent>
            </Card>

            {/* History range tabs */}
            <Tabs value={selectedRange} onValueChange={handleRangeChange} className="mb-6">
                <TabsList className="w-full">
                    <TabsTrigger value="7" className="flex-1">7 days</TabsTrigger>
                    <TabsTrigger value="30" className="flex-1">30 days</TabsTrigger>
                    <TabsTrigger value="90" className="flex-1 relative">
                        90 days
                        {!isPro && <Lock className="w-3 h-3 ml-1" />}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="7" className="mt-4">
                    <HistoryGrid days={7} progress={progress} color={color} />
                </TabsContent>

                <TabsContent value="30" className="mt-4">
                    <HistoryGrid days={30} progress={progress} color={color} />
                </TabsContent>

                <TabsContent value="90" className="mt-4">
                    {isPro ? (
                        <HistoryGrid days={90} progress={progress} color={color} />
                    ) : (
                        <div className="text-center py-8">
                            <Lock className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                            <p className="text-muted-foreground text-sm">Upgrade to Pro</p>
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            {/* Items list */}
            <Card className="mb-6">
                <CardContent className="p-4">
                    <h3 className="text-xs font-medium text-muted-foreground mb-3">
                        Items ({stack.items.length})
                    </h3>
                    <ul className="space-y-2">
                        {stack.items
                            .sort((a, b) => a.position - b.position)
                            .map((item, index) => (
                                <li key={item.id} className="flex items-start gap-2 text-sm">
                                    <span className="text-muted-foreground w-4 text-right text-xs mt-0.5">{index + 1}.</span>
                                    <div>
                                        <p className="text-foreground">{item.name}</p>
                                        {item.minimum_version && (
                                            <p className="text-xs text-muted-foreground">{item.minimum_version}</p>
                                        )}
                                    </div>
                                </li>
                            ))}
                    </ul>
                </CardContent>
            </Card>

            {/* Archive button */}
            <AlertDialog>
                <AlertDialogTrigger asChild>
                    <Button variant="outline" className="w-full gap-2" disabled={archiving}>
                        {archiving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                        Archive Stack
                    </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Archive {stack.name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            You can restore it later from Settings.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleArchive}>
                            Archive
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function HistoryGrid({ days, progress, color }) {
    const gridData = [];
    const today = new Date();

    for (let i = days - 1; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];

        const dayData = progress.history7Day?.find(d => d.date === dateStr);
        gridData.push({
            date: dateStr,
            ran: dayData?.ran || false,
            items: dayData?.itemsCompleted || 0,
        });
    }

    const cellSize = days === 7 ? 'w-7 h-7' : days === 30 ? 'w-5 h-5' : 'w-3 h-3';

    return (
        <div className="flex flex-wrap gap-1 justify-center">
            {gridData.map((day) => (
                <div
                    key={day.date}
                    className={cn(
                        cellSize,
                        "rounded transition-all",
                        day.ran
                            ? color.class
                            : "bg-border"
                    )}
                    title={`${day.date}: ${day.items} items`}
                />
            ))}
        </div>
    );
}
